import { type ReactElement, type ReactNode } from 'react';
import { iconSizes } from '@/theme/tokens';
import { Stack } from '@/ui/layout/Stack';
import { Box, CircularProgress, Text } from '@/ui/primitives';
import { PageStateScaffold } from './PageStateScaffold';
import type { ErrorInput } from '@/shared/data/errors';
import type { UiTone } from '@/theme/uiTokens';

type DetailPageScaffoldProps<TData> = {
  session: object | null;
  sessionMessage: string;
  organisationId?: string | null;
  requireProject?: boolean;
  projectMessage?: string;
  projectId?: string | null;
  isPending: boolean;
  isError: boolean;
  error: ErrorInput;
  errorMessage: string;
  data: TData | null | undefined;
  loadingFallback?: ReactNode;
  redirecting?: boolean;
  redirectMessage?: string;
  emptyMessage?: string;
  emptyTone?: UiTone;
  children: (data: TData) => ReactNode;
};

export const DetailPageScaffold = <TData,>({
  session,
  sessionMessage,
  organisationId,
  requireProject = false,
  projectMessage,
  projectId,
  isPending,
  isError,
  error,
  errorMessage,
  data,
  loadingFallback,
  redirecting,
  redirectMessage,
  emptyMessage,
  emptyTone = 'blue',
  children,
}: DetailPageScaffoldProps<TData>): ReactElement => {
  const isEmpty = !data;
  const resolvedLoadingFallback = loadingFallback ?? (
    <Box
      sx={{
        minHeight: '45vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack gap="md" align="center">
        <Text role="body" color="textSecondary" align="center">
          Loading details...
        </Text>
        <CircularProgress size={iconSizes.lg} />
      </Stack>
    </Box>
  );

  return (
    <PageStateScaffold
      session={session}
      sessionMessage={sessionMessage}
      organisationId={organisationId}
      requireProject={requireProject}
      projectMessage={projectMessage}
      projectId={projectId}
      isLoading={isPending}
      isError={isError}
      error={error}
      errorMessage={errorMessage}
      loadingFallback={resolvedLoadingFallback}
      redirecting={redirecting}
      redirectMessage={redirectMessage}
      empty={isEmpty}
      emptyMessage={emptyMessage}
      emptyTone={emptyTone}
      variant="card"
    >
      {data ? children(data) : null}
    </PageStateScaffold>
  );
};
